import { BarChart3, ChevronDown, ChevronRight, Download, Edit, Folder, FolderOpen, GripVertical, MessageCircle, Play, Plus, Settings, Target, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api';
import { useProject } from '../../contexts/ProjectContext';
import { getStepNumber } from '../../utils/stepNumbering';
import EnhancedStepChatModal from '../EnhancedStepChatModal';
import ProcessSynthesisLoading from './ProcessSynthesisLoading';
import { Info } from 'lucide-react';
import Tooltip from './Tooltip';


interface ProcessStep {
  step: string;
  sources?: any[];
  inference?: string;
  is_expanded?: boolean;
  parent_step_index?: number;
  conversation_derived?: boolean;
  created_at?: string;
  section_id?: string;
}

interface ProcessSection {
  id: string;
  name: string;
  description?: string;
  color: string;
  order: number;
  step_count?: number;
}

const EnhancedProcessSynthesisTab: React.FC<{
  analysisResults: any;
  isAnalyzing: boolean;
  analysisProgress: any;
  globalComplianceScore: number | null;
  onRefresh: () => void;
}> = ({ analysisResults, isAnalyzing, analysisProgress, globalComplianceScore, onRefresh }) => {
  const { currentProject } = useProject();

  // Extract synthesis data
  const synthesis = useMemo(() => analysisResults?.process_synthesis || {}, [analysisResults]);
  const synthesisData = useMemo(() => {
    if (synthesis?.synthesis_text) {
      try {
        return typeof synthesis.synthesis_text === 'string' ? JSON.parse(synthesis.synthesis_text) : synthesis.synthesis_text;
      } catch {
        return synthesis;
      }
    }
    return synthesis;
  }, [synthesis]);

  // State management for existing cards
  const [stakeholders, setStakeholders] = useState<{ [key: string]: any }>({});
  const [editingStakeholder, setEditingStakeholder] = useState<string | null>(null);
  const [editStakeholderValue, setEditStakeholderValue] = useState({ role: '', responsibility: '' });
  const [showAddStakeholder, setShowAddStakeholder] = useState(false);
  const [newStakeholder, setNewStakeholder] = useState({ role: '', responsibility: '' });
  const [selectedStakeholders, setSelectedStakeholders] = useState<string[]>([]);

  const [systems, setSystems] = useState<{ [key: string]: any }>({});
  const [editingSystem, setEditingSystem] = useState<string | null>(null);
  const [editSystemValue, setEditSystemValue] = useState({ system: '', purpose: '' });
  const [showAddSystem, setShowAddSystem] = useState(false);
  const [newSystem, setNewSystem] = useState({ system: '', purpose: '' });
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);

  // State management for MANUAL sections and step functionality
  const [steps, setSteps] = useState<(string | ProcessStep)[]>([]);
  const [sections, setSections] = useState<ProcessSection[]>([]);
  const [sectionsMap, setSectionsMap] = useState<Map<string, string>>(new Map());
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Unassigned steps (not yet placed in any section)
  const [unassignedStepsExpanded, setUnassignedStepsExpanded] = useState(true);
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [newStepValue, setNewStepValue] = useState('');

  // Chat functionality state
  const [chatStepIdx, setChatStepIdx] = useState<number | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  const [draggedStepId, setDraggedStepId] = useState<number | null>(null);
  const [draggedFromSection, setDraggedFromSection] = useState<string | null>(null);
  const [dragOverUnassignedIndex, setDragOverUnassignedIndex] = useState<number | null>(null);

  const [sectionStepOrder, setSectionStepOrder] = useState<Map<string, number[]>>(new Map());

  const analysisHasRun = analysisProgress && (analysisProgress.completed > 0 || analysisProgress.failed > 0);
  const isWaitingForSynthesis = !isAnalyzing && analysisHasRun && (!synthesis || !synthesisData?.end_to_end_process);
  const shouldShowLoading = isAnalyzing || isWaitingForSynthesis || !synthesis || !synthesisData?.end_to_end_process;

  // All state and hooks are defined above - no conditional returns before hooks

  // Load steps and existing card data
  const saveStakeholders = async (updatedStakeholders: { [key: string]: any }) => {
    if (!currentProject?.id) return;

    try {
      await api.put(`/api/analysis/stakeholders/${currentProject.id}`, {
        stakeholders: updatedStakeholders
      });
    } catch (error) {
      console.error('Error saving stakeholders:', error);
    }
  };

  const saveSystems = async (updatedSystems: { [key: string]: any }) => {
    if (!currentProject?.id) return;

    try {
      await api.put(`/api/analysis/systems/${currentProject.id}`, {
        systems: updatedSystems
      });
    } catch (error) {
      console.error('Error saving systems:', error);
    }
  };

  // Save sections to backend
  const saveSectionsToBackend = async (sectionsToSave: ProcessSection[], sectionsMapToSave: Map<string, string>) => {
    if (!currentProject?.id) return;

    try {
      setIsSaving(true);
      const sectionsMapObj = Object.fromEntries(sectionsMapToSave);

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sectionsToSave,
        sectionsMap: sectionsMapObj
      });

      toast.success('Sections saved successfully');
    } catch (error) {
      console.error('Error saving sections:', error);
      toast.error('Failed to save sections');
    } finally {
      setIsSaving(false);
    }
  };

  // Export Process Synthesis to Excel
  const exportToExcel = async () => {
    if (!currentProject?.id || steps.length === 0) {
      toast.error('No process steps to export');
      return;
    }

    const exportToastId = toast.loading('Preparing Process Synthesis Excel...');

    try {
      setIsExporting(true);

      // Convert sectionsMap to plain object
      const sectionsMapObj = Object.fromEntries(sectionsMap);

      const response = await api.post(`/api/export/process-synthesis-excel/${currentProject.id}`, {
        steps,
        sections,
        sectionsMap: sectionsMapObj,
        stakeholders,
        systems
      });

      toast.loading('Downloading Excel file...', { id: exportToastId });

      // Download the file
      const downloadUrl = response.data.download_url;
      const fileResponse = await api.get(downloadUrl, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([fileResponse.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Process Synthesis exported successfully!', { id: exportToastId });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export Process Synthesis', { id: exportToastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Stakeholder management functions (keeping existing logic)
  const startEditStakeholder = (role: string) => {
    setEditingStakeholder(role);
    const currentResp = stakeholders[role];
    const responsibilityText = typeof currentResp === 'string' ? currentResp :
      typeof currentResp === 'object' && currentResp !== null ?
        (currentResp as any).responsibility || (currentResp as any).description || JSON.stringify(currentResp) :
        String(currentResp || '');
    setEditStakeholderValue({ role, responsibility: responsibilityText });
  };

  const saveStakeholderEdit = () => {
    if (!editingStakeholder) return;
    const updated = { ...stakeholders };
    if (editStakeholderValue.role !== editingStakeholder) {
      delete updated[editingStakeholder];
    }
    updated[editStakeholderValue.role] = editStakeholderValue.responsibility;
    setStakeholders(updated);
    saveStakeholders(updated);
    setEditingStakeholder(null);
  };

  const deleteStakeholder = (role: string) => {
    const updated = { ...stakeholders };
    delete updated[role];
    setStakeholders(updated);
    saveStakeholders(updated);
  };

  const addStakeholder = () => {
    if (!newStakeholder.role.trim() || !newStakeholder.responsibility.trim()) return;
    const updated = { ...stakeholders, [newStakeholder.role]: newStakeholder.responsibility };
    setStakeholders(updated);
    saveStakeholders(updated);
    setNewStakeholder({ role: '', responsibility: '' });
    setShowAddStakeholder(false);
  };

  const mergeStakeholders = () => {
    if (selectedStakeholders.length < 2) return;

    // Collect all individual name parts from selected entries (handles already-merged keys like "A / B")
    const allParts = new Set<string>();
    selectedStakeholders.forEach(role => {
      role.split(/\s*[&\/]\s*/).forEach(part => allParts.add(part.trim()));
    });

    const mergedRole = Array.from(allParts).join(' & ');
    const mergedResponsibility = selectedStakeholders.map(role => {
      const resp = stakeholders[role];
      return typeof resp === 'string' ? resp :
        typeof resp === 'object' && resp !== null ?
          (resp as any).responsibility || (resp as any).description || JSON.stringify(resp) :
          String(resp || '');
    }).join('; ');

    const updated = { ...stakeholders };

    // Delete all directly selected roles
    selectedStakeholders.forEach(role => delete updated[role]);

    // Also delete any existing composite keys whose parts are all subsets of the new merge
    // (cleans up stale partial-merge entries)
    Object.keys(updated).forEach(key => {
      const keyParts = key.split(/\s*[&\/]\s*/).map(p => p.trim());
      if (keyParts.length > 1 && keyParts.every(part => allParts.has(part))) {
        delete updated[key];
      }
    });

    updated[mergedRole] = mergedResponsibility;
    setStakeholders(updated);
    saveStakeholders(updated);
    setSelectedStakeholders([]);
  };

  // System management functions (keeping existing logic)
  const startEditSystem = (system: string) => {
    setEditingSystem(system);
    const currentPurpose = systems[system];
    const purposeText = typeof currentPurpose === 'string' ? currentPurpose :
      typeof currentPurpose === 'object' && currentPurpose !== null ?
        (currentPurpose as any).purpose || (currentPurpose as any).description || JSON.stringify(currentPurpose) :
        String(currentPurpose || '');
    setEditSystemValue({ system, purpose: purposeText });
  };

  const saveSystemEdit = () => {
    if (!editingSystem) return;
    const updated = { ...systems };
    if (editSystemValue.system !== editingSystem) {
      delete updated[editingSystem];
    }
    updated[editSystemValue.system] = editSystemValue.purpose;
    setSystems(updated);
    saveSystems(updated);
    setEditingSystem(null);
  };

  const deleteSystem = (system: string) => {
    const updated = { ...systems };
    delete updated[system];
    setSystems(updated);
    saveSystems(updated);
  };

  const addSystem = () => {
    if (!newSystem.system.trim() || !newSystem.purpose.trim()) return;
    const updated = { ...systems, [newSystem.system]: newSystem.purpose };
    setSystems(updated);
    saveSystems(updated);
    setNewSystem({ system: '', purpose: '' });
    setShowAddSystem(false);
  };

  const mergeSystems = () => {
    if (selectedSystems.length < 2) return;

    const allParts = new Set<string>();
    selectedSystems.forEach(sys => {
      sys.split(/\s*[&\/]\s*/).forEach(part => allParts.add(part.trim()));
    });

    const mergedSystem = Array.from(allParts).join(' & ');
    const mergedPurpose = selectedSystems.map(system => {
      const purpose = systems[system];
      return typeof purpose === 'string' ? purpose :
        typeof purpose === 'object' && purpose !== null ?
          (purpose as any).purpose || (purpose as any).description || JSON.stringify(purpose) :
          String(purpose || '');
    }).join('; ');

    const updated = { ...systems };
    selectedSystems.forEach(system => delete updated[system]);

    Object.keys(updated).forEach(key => {
      const keyParts = key.split(/\s*[&\/]\s*/).map(p => p.trim());
      if (keyParts.length > 1 && keyParts.every(part => allParts.has(part))) {
        delete updated[key];
      }
    });

    updated[mergedSystem] = mergedPurpose;
    setSystems(updated);
    saveSystems(updated);
    setSelectedSystems([]);
  };

  // Load steps and sections from backend
  useEffect(() => {
    console.log('📥 Loading sections and sections map from backend...', {
      'synthesis.sections exists': !!synthesis?.sections,
      'synthesis.sections length': synthesis?.sections?.length || 0,
      'synthesis.sections_map exists': !!synthesis?.sections_map,
    });

    // Load sections from backend
    if (synthesis?.sections && Array.isArray(synthesis.sections) && synthesis.sections.length > 0) {
      console.log('📥 Loading sections from backend:', synthesis.sections.length, 'sections');
      setSections(synthesis.sections);
    }

    // Load sections map from backend
    if (synthesis?.sections_map) {
      console.log('📥 Loading sections map from backend');
      const newSectionsMap = new Map();
      Object.entries(synthesis.sections_map).forEach(([key, value]) => {
        newSectionsMap.set(key, value as string);
      });
      setSectionsMap(newSectionsMap);
    }
  }, [
    synthesis?.sections,
    synthesis?.sections_map,
    JSON.stringify(synthesis?.sections),
    JSON.stringify(synthesis?.sections_map)
  ]);

  useEffect(() => {
    console.log('📥 Loading steps from backend...', {
      'synthesis.process_steps exists': !!synthesis?.process_steps,
      'synthesis.process_steps length': synthesis?.process_steps?.length || 0
    });

    let stepsArr: any[] = [];

    // PRIORITY 1: Use process_steps from backend
    if (synthesis?.process_steps && Array.isArray(synthesis.process_steps) && synthesis.process_steps.length > 0) {
      console.log('📥 Loading from synthesis.process_steps:', synthesis.process_steps.length, 'steps');
      stepsArr = synthesis.process_steps;
    }
    // PRIORITY 2: Fallback to synthesis_text
    else if (synthesis?.synthesis_text) {
      console.log('📥 Falling back to synthesis_text parsing...');
      let synthesisData;
      try {
        synthesisData = typeof synthesis.synthesis_text === 'string'
          ? JSON.parse(synthesis.synthesis_text)
          : synthesis.synthesis_text;
      } catch (error) {
        console.error('Failed to parse synthesis_text:', error);
        synthesisData = synthesis.synthesis_text;
      }

      if (synthesisData?.end_to_end_process && Array.isArray(synthesisData.end_to_end_process)) {
        console.log('📥 Using end_to_end_process fallback:', synthesisData.end_to_end_process.length, 'steps');
        stepsArr = synthesisData.end_to_end_process;
      }
    }

    if (stepsArr && stepsArr.length > 0) {
      console.log('✅ Setting steps:', stepsArr.length);
      setSteps(stepsArr);
    } else {
      console.log('❌ No steps found - clearing steps array');
      setSteps([]);
    }
  }, [
    synthesis?.process_steps,
    synthesis?.synthesis_text,
    JSON.stringify(synthesis?.process_steps)
  ]);

  const addSection = useCallback(async () => {
    if (!newSectionName.trim() || !currentProject?.id) return;

    try {
      console.log('➕ Adding new section:', newSectionName);

      const newSection: ProcessSection = {
        id: `section_${Date.now()}`,
        name: newSectionName.trim(),
        description: newSectionDescription.trim(),
        color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
        order: sections.length,
        step_count: 0
      };

      const updatedSections = [...sections, newSection];
      setSections(updatedSections);

      // ✨ If this is the first section and we have unassigned steps, assign them
      let updatedSectionsMap = new Map(sectionsMap);
      
      // if (sections.length === 0 && steps.length > 0) {
      //   console.log('🎯 First section - auto-assigning all steps');
        
      //   steps.forEach((step, index) => {
      //     updatedSectionsMap.set(index.toString(), newSection.id);
      //   });
        
      //   setSectionsMap(updatedSectionsMap);
        
      //   console.log('📍 Auto-assigned', steps.length, 'steps to new section');
      // }

      // Save to backend
      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: updatedSections,
        sectionsMap: Object.fromEntries(updatedSectionsMap),
        business_type: synthesis?.business_type || 'general'
      });

      console.log('✅ New section saved successfully');

      // Force refresh to sync all tabs
      if (onRefresh) {
        console.log('🔄 Triggering global refresh after section add...');
        await onRefresh();
      }

      setNewSectionName('');
      setNewSectionDescription('');
      setShowAddSection(false);
      toast.success('Section added and steps assigned successfully');
      
    } catch (error) {
      console.error('❌ Error adding section:', error);
      toast.error('Failed to add section');
      setSections(sections);
    }
  }, [newSectionName, newSectionDescription, currentProject?.id, sections, sectionsMap, steps, synthesis?.business_type, onRefresh]);
  
  const deleteSection = useCallback(async (sectionId: string) => {
    if (!currentProject?.id) return;

    try {
      console.log('🗑️ Deleting section:', sectionId);

      // Remove section
      const updatedSections = sections.filter(s => s.id !== sectionId);
      setSections(updatedSections);

      // Remove assignments from sectionsMap
      const newSectionsMap = new Map();
      sectionsMap.forEach((assignedSectionId, stepIndex) => {
        if (assignedSectionId !== sectionId) {
          newSectionsMap.set(stepIndex, assignedSectionId);
        }
      });
      setSectionsMap(newSectionsMap);

      // Save to backend
      console.log('💾 Saving after section deletion');

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: updatedSections,
        sectionsMap: Object.fromEntries(newSectionsMap),
        business_type: synthesis?.business_type || 'general'
      });

      console.log('✅ Section deletion saved successfully');

      // CRITICAL: Force refresh to sync all tabs
      if (onRefresh) {
        console.log('🔄 Triggering global refresh after section delete...');
        await onRefresh();
      }

      toast.success('Section deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting section:', error);
      toast.error('Failed to delete section');

      // Revert local state on error
      setSections(sections);
      setSectionsMap(sectionsMap);
    }
  }, [currentProject?.id, sections, sectionsMap, synthesis?.business_type, onRefresh]);

  // Step management functions
  const deleteStep = async (stepIndex: number) => {
  if (!currentProject?.id) {
    toast.error('No project selected');
    return;
  }

  try {
    console.log('🗑️ EnhancedProcessSynthesisTab: Deleting step:', stepIndex);

    // Update local state immediately
    const updatedSteps = steps.filter((_, index) => index !== stepIndex);
    setSteps(updatedSteps);

    // CRITICAL FIX: Update sections map properly - shift indices for steps after deleted step
    const newSectionsMap = new Map();
    sectionsMap.forEach((sectionId, indexStr) => {
      const index = parseInt(indexStr);
      if (index < stepIndex) {
        // Keep steps before deleted step with same index
        newSectionsMap.set(indexStr, sectionId);
      } else if (index > stepIndex) {
        // Shift steps after deleted step down by 1
        newSectionsMap.set((index - 1).toString(), sectionId);
      }
      // Skip the deleted step (index === stepIndex)
    });
    setSectionsMap(newSectionsMap);

    // CRITICAL FIX: Update section step order properly
    const newSectionStepOrder = new Map();
    sectionStepOrder.forEach((order, sectionId) => {
      // Filter out the deleted step and shift indices
      const updatedOrder = order
        .filter(idx => idx !== stepIndex) // Remove deleted step
        .map(idx => idx > stepIndex ? idx - 1 : idx); // Shift indices after deleted step
      
      newSectionStepOrder.set(sectionId, updatedOrder);
    });
    setSectionStepOrder(newSectionStepOrder);

    // CRITICAL: Save steps to backend
    await api.put(`/api/analysis/process-steps/${currentProject.id}`, {
      steps: updatedSteps
    });

    // Save sections map to backend if we have sections
    if (sections.length > 0) {
      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(newSectionsMap),
        business_type: synthesis?.business_type || 'general',
        section_step_order: Object.fromEntries(newSectionStepOrder)
      });
    }

    console.log('✅ EnhancedProcessSynthesisTab: Step deletion saved successfully');

    // CRITICAL: Trigger global refresh for tab synchronization
    if (onRefresh) {
      console.log('🔄 EnhancedProcessSynthesisTab: Triggering global refresh after step delete...');
      setTimeout(() => {
        onRefresh();
      }, 200);
    }

    toast.success('Step deleted and synchronized across tabs', {
      duration: 3000,
      icon: '🗑️'
    });

  } catch (error) {
    console.error('❌ EnhancedProcessSynthesisTab: Error deleting step:', error);
    toast.error('Failed to delete step. Please try again.');

    // Revert local state on error
    setSteps(steps);
    setSectionsMap(sectionsMap);
    setSectionStepOrder(sectionStepOrder);
  }
};

  const addStep = async () => {
    const text = newStepValue.trim();
    if (!text || !currentProject?.id) return;

    const newStep: ProcessStep = {
      step: text,
      sources: [],
      inference: 'Manually added step',
      conversation_derived: false,
      created_at: new Date().toISOString(),
    };

    const updatedSteps = [...steps, newStep];
    setSteps(updatedSteps);
    setNewStepValue('');
    setIsAddingStep(false);

    try {
      await api.put(`/api/analysis/process-steps/${currentProject.id}`, { steps: updatedSteps });
      toast.success('Step added successfully');
      if (onRefresh) setTimeout(() => onRefresh(), 200);
    } catch (error) {
      console.error('Error adding step:', error);
      toast.error('Failed to save step');
      setSteps(steps);
    }
  };

  useEffect(() => {
    console.log('📊 STATE CHANGE DEBUG:', {
      'Steps count': steps.length,
      'Sections count': sections.length,
      'SectionsMap entries': sectionsMap.size,
      'Current project': currentProject?.id,
      'Has onRefresh': !!onRefresh,
      'Timestamp': new Date().toISOString()
    });
  }, [steps.length, sections.length, sectionsMap.size, currentProject?.id]);

  const moveStepToSection = useCallback(async (stepIndex: number, targetSectionId: string) => {
    if (!currentProject?.id) return;

    try {
      console.log('🔄 Moving step', stepIndex, 'to section', targetSectionId);

      // Update local sectionsMap immediately
      const newSectionsMap = new Map(sectionsMap);
      newSectionsMap.set(stepIndex.toString(), targetSectionId);
      setSectionsMap(newSectionsMap);

      // IMPORTANT: Also update section_step_order to include this step at the end of target section
      const newSectionStepOrder = new Map(sectionStepOrder);
      const targetSectionOrder = newSectionStepOrder.get(targetSectionId) || [];
      
      // Remove step from any existing section orders
      newSectionStepOrder.forEach((order, sectionId) => {
        const filteredOrder = order.filter(idx => idx !== stepIndex);
        newSectionStepOrder.set(sectionId, filteredOrder);
      });
      
      // Add step to end of target section
      const updatedTargetOrder = [...targetSectionOrder.filter(idx => idx !== stepIndex), stepIndex];
      newSectionStepOrder.set(targetSectionId, updatedTargetOrder);
      setSectionStepOrder(newSectionStepOrder);

      // Save both maps to backend
      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(newSectionsMap),
        section_step_order: Object.fromEntries(newSectionStepOrder),  // ✅ Include this!
        business_type: synthesis?.business_type || 'general'
      });

      console.log('✅ Step moved and section order updated');

      if (onRefresh) {
        setTimeout(() => onRefresh(), 200);
      }

      toast.success('Step moved successfully', {
        duration: 2000,
        icon: '📁'
      });

    } catch (error) {
      console.error('❌ Error moving step to section:', error);
      toast.error('Failed to move step');
      
      // Revert local state on error
      setSectionsMap(sectionsMap);
      setSectionStepOrder(sectionStepOrder);
    }
  }, [currentProject?.id, sectionsMap, sectionStepOrder, sections, synthesis?.business_type, onRefresh]);

  // Enhanced drag and drop handling
  const handleDragStart = (e: React.DragEvent, stepIndex: number) => {
    e.dataTransfer.setData('text/plain', stepIndex.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();
    const stepIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (!isNaN(stepIndex)) {
      console.log('🎯 Dropped step', stepIndex, 'onto section', targetSectionId);
      await moveStepToSection(stepIndex, targetSectionId);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Get unassigned steps
  const unassignedSteps = useMemo(() => {
    return steps
      .map((step, index) => ({ step, index }))
      .filter(({ index }) => !sectionsMap.has(index.toString()));
  }, [steps, sectionsMap]);

  useEffect(() => {
    console.log('🔄 CRITICAL: Loading section step order from backend...', {
      'synthesis?.section_step_order exists': !!synthesis?.section_step_order,
      'synthesis?.section_step_order': synthesis?.section_step_order,
      'current sectionStepOrder.size': sectionStepOrder.size
    });

    // Load section step order from backend
    if (synthesis?.section_step_order) {
      console.log('📥 LOADING section step order from backend:', synthesis.section_step_order);
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
          console.log(`📋 Loaded order for section ${sectionId}:`, order);
        }
      });
      setSectionStepOrder(orderMap);
      console.log('✅ Section step order loaded:', Object.fromEntries(orderMap));
    } else {
      console.log('⚠️ No section_step_order found in synthesis');
    }
  }, [synthesis?.section_step_order, JSON.stringify(synthesis?.section_step_order)]);

  // Group steps by section
  const stepsBySection = useMemo(() => {
    console.log('🔄 Computing stepsBySection with PROPER ORDER...', {
      'steps.length': steps.length,
      'sections.length': sections.length,
      'sectionsMap.size': sectionsMap.size,
      'sectionStepOrder.size': sectionStepOrder.size,
      'sectionStepOrder content': Object.fromEntries(sectionStepOrder)
    });

    const grouped = new Map<string, Array<{ step: string | ProcessStep; index: number }>>();
    
    // Initialize empty arrays for all sections
    sections.forEach(section => {
      grouped.set(section.id, []);
    });
    
    // First, collect all steps that belong to sections according to sectionsMap
    const stepsInSections: Array<{ step: string | ProcessStep; index: number; sectionId: string }> = [];
    
    steps.forEach((step, index) => {
      const sectionId = sectionsMap.get(index.toString());
      if (sectionId && grouped.has(sectionId)) {
        stepsInSections.push({ step, index, sectionId });
      }
    });
    
    console.log('📊 Steps in sections before ordering:', stepsInSections.map(s => ({
      stepIndex: s.index,
      sectionId: s.sectionId
    })));
    
    // Apply section-specific ordering
    sections.forEach(section => {
      const sectionSteps = stepsInSections.filter(s => s.sectionId === section.id);
      const customOrder = sectionStepOrder.get(section.id);
      
      if (customOrder && Array.isArray(customOrder) && customOrder.length > 0) {
        console.log(`📋 APPLYING custom order for section ${section.id}:`, customOrder);
        
        // Apply the exact custom order
        const reorderedSteps: Array<{ step: string | ProcessStep; index: number }> = [];
        
        // Add steps in their exact custom order
        customOrder.forEach(originalIndex => {
          const stepData = sectionSteps.find(s => s.index === originalIndex);
          if (stepData) {
            reorderedSteps.push({ step: stepData.step, index: stepData.index });
            console.log(`✅ Ordered step ${originalIndex} in position ${reorderedSteps.length}`);
          } else {
            console.log(`⚠️ Step ${originalIndex} not found in section ${section.id}`);
          }
        });
        
        // Add any NEW steps that aren't in the custom order yet
        sectionSteps.forEach(stepData => {
          if (!customOrder.includes(stepData.index)) {
            reorderedSteps.push({ step: stepData.step, index: stepData.index });
            console.log(`🆕 Added new step ${stepData.index} to end of section ${section.id}`);
          }
        });
        
        grouped.set(section.id, reorderedSteps);
        console.log(`✅ Final order for section ${section.id}:`, reorderedSteps.map(s => s.index));
        
      } else {
        // No custom order - use natural order (by index)
        sectionSteps.sort((a, b) => a.index - b.index);
        const naturalOrderSteps = sectionSteps.map(s => ({ step: s.step, index: s.index }));
        grouped.set(section.id, naturalOrderSteps);
        
        console.log(`📝 No custom order for section ${section.id}, using natural order:`, naturalOrderSteps.map(s => s.index));
      }
    });
    
    console.log('✅ stepsBySection computed with proper ordering:', Array.from(grouped.entries()).map(([id, steps]) => ({
      sectionId: id,
      stepIndices: steps.map(s => s.index),
      stepCount: steps.length
    })));
    
    return grouped;
  }, [steps, sections, sectionsMap, sectionStepOrder]);

  useEffect(() => {
    const handleFocus = async () => {
      if (currentProject?.id && onRefresh) {
        console.log('🔍 Tab focused, checking for data updates...');

        // Small delay to ensure any background saves are complete
        setTimeout(() => {
          onRefresh();
        }, 500);
      }
    };

      // Listen for window focus events
    window.addEventListener('focus', handleFocus);

    // Also listen for tab visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden && currentProject?.id && onRefresh) {
        console.log('👁️ Tab became visible, refreshing data...');
        setTimeout(() => {
          onRefresh();
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentProject?.id, onRefresh]);

  useEffect(() => {
    // When this component mounts/remounts (like when switching back to this tab)
    // Always refresh the data to get the latest state
    if (currentProject?.id && onRefresh) {
      console.log('🔄 EnhancedProcessSynthesisTab mounted, refreshing data...');
      onRefresh();
    }
  }, []);

  const getSequentialStepNumber = (stepIndex: number, sectionId?: string) => {
    if (sectionId) {
      // Get all steps for this specific section in their CURRENT DISPLAY ORDER
      const sectionSteps = stepsBySection.get(sectionId) || [];
      
      // Find the current step object to check if it has parent_step_index
      const currentStepObj = steps[stepIndex];
      
      // Check if this is an expanded/sub-step by looking at parent_step_index
      if (typeof currentStepObj === 'object' && currentStepObj.parent_step_index !== undefined) {
        // This is a sub-step - find its parent step number
        const parentStepIndex = currentStepObj.parent_step_index;
        const parentStepNumber = parentStepIndex + 1; // Convert index to step number
        
        // Find all steps in this section that have the same parent_step_index
        const siblingSteps = sectionSteps.filter(({ index }) => {
          const stepObj = steps[index];
          return typeof stepObj === 'object' && stepObj.parent_step_index === parentStepIndex;
        });
        
        // Find the position of current step among its siblings
        const siblingPosition = siblingSteps.findIndex(({ index }) => index === stepIndex);
        
        // Return parent number + sub-number (e.g., "2.1", "2.2", "2.3")
        return `${parentStepNumber}.${siblingPosition + 1}`;
      } else {
        // This is a main step - need to count ALL main steps across ALL sections up to this point
        // to get proper sequential numbering (1, 2, 3, 4, 5, etc.)
        
        // Get all steps from all sections in order
        const allMainSteps: Array<{index: number, sectionId: string}> = [];
        
        // Go through all sections and collect main steps in order
        sections.forEach(section => {
          const sectionSteps = stepsBySection.get(section.id) || [];
          sectionSteps.forEach(({ index }) => {
            const stepObj = steps[index];
            // Only include main steps (those without parent_step_index)
            if (!(typeof stepObj === 'object' && stepObj.parent_step_index !== undefined)) {
              allMainSteps.push({ index, sectionId: section.id });
            }
          });
        });
        
        // Find the position of this step among all main steps
        const mainStepPosition = allMainSteps.findIndex(({ index }) => index === stepIndex);
        
        if (mainStepPosition !== -1) {
          // Return sequential numbering: 1, 2, 3, 4, 5, etc.
          return mainStepPosition + 1;
        }
        
        // Fallback
        return stepIndex + 1;
      }
    }
    
    // Fallback to original logic for unassigned steps
    return getStepNumber(steps, stepIndex);
  };

  const handleSectionStepDragStart = (e: React.DragEvent, stepIndex: number, sectionId: string) => {
    console.log('🎯 Section drag start - Step:', stepIndex, 'Section:', sectionId);

    setDraggedStepId(stepIndex);
    setDraggedFromSection(sectionId);

    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'section-step',
      stepIndex: stepIndex,
      sectionId: sectionId,
      source: 'section'
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionContainerDrop = async (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      console.log('📦 Section container drop:', dragData, 'Target section:', targetSectionId);

      if (dragData.type === 'main-flow-step' || dragData.type === 'section-step') {
        // This is moving a step TO a section (from main flow or from another section)
        await moveStepToSection(dragData.stepIndex, targetSectionId);
      }
    } catch (error) {
      console.error('❌ Error in section container drop:', error);
    } finally {
      setDraggedStepId(null);
      setDraggedFromSection(null);
    }
  };

  const handleSectionStepDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionStepDrop = async (e: React.DragEvent, targetStepIndex: number, targetSectionId: string) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent the container drop from also firing

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      console.log('📍 Section step drop:', dragData, 'Target step:', targetStepIndex, 'Section:', targetSectionId);

      if (dragData.type === 'section-step' && dragData.sectionId === targetSectionId) {
        // This is reordering within the same section
        if (dragData.stepIndex !== targetStepIndex) {
          await reorderStepsInSection(dragData.stepIndex, targetStepIndex, targetSectionId);
        }
      } else if (dragData.type === 'main-flow-step' ||
        (dragData.type === 'section-step' && dragData.sectionId !== targetSectionId)) {
        // This is moving a step TO this section
        await moveStepToSection(dragData.stepIndex, targetSectionId);
      }
    } catch (error) {
      console.error('❌ Error in section step drop:', error);
    } finally {
      setDraggedStepId(null);
      setDraggedFromSection(null);
    }
  };


  const reorderStepsInSection = useCallback(async (fromStepIndex: number, toStepIndex: number, sectionId: string) => {
  if (!currentProject?.id) return;

  console.log('🔄 ENHANCED REORDERING within section:', sectionId);
  console.log('🔄 From step:', fromStepIndex, 'To step:', toStepIndex);

  try {
    // Get current section steps in their display order
    const currentSectionSteps = stepsBySection.get(sectionId) || [];
    console.log('📋 Current section steps before reorder:', currentSectionSteps.map(s => s.index));

    // Get current order from sectionStepOrder, fallback to natural order
    let currentOrder = sectionStepOrder.get(sectionId);
    if (!currentOrder || currentOrder.length === 0) {
      // Create initial order from current steps
      currentOrder = currentSectionSteps.map(s => s.index).sort((a, b) => a - b);
      console.log('📝 Created initial order from current steps:', currentOrder);
    }

    // Find positions in the current order
    const fromPosition = currentOrder.indexOf(fromStepIndex);
    const toPosition = currentOrder.indexOf(toStepIndex);

    if (fromPosition === -1 || toPosition === -1) {
      console.error('❌ Could not find step positions in current order');
      console.error('❌ fromStepIndex:', fromStepIndex, 'found at:', fromPosition);
      console.error('❌ toStepIndex:', toStepIndex, 'found at:', toPosition);
      console.error('❌ currentOrder:', currentOrder);
      return;
    }

    // Create new order by moving the step
    const newOrder = [...currentOrder];
    const [movedStepIndex] = newOrder.splice(fromPosition, 1);
    newOrder.splice(toPosition, 0, movedStepIndex);

    console.log('🔄 REORDER DETAILS:');
    console.log('  - Original order:', currentOrder);
    console.log('  - Moving step:', movedStepIndex, 'from position', fromPosition, 'to position', toPosition);
    console.log('  - New order:', newOrder);

    // Update the section step order map
    const newSectionStepOrder = new Map(sectionStepOrder);
    newSectionStepOrder.set(sectionId, newOrder);
    setSectionStepOrder(newSectionStepOrder);

    // Save to backend immediately with all required data
    const saveData = {
      sections: sections,
      sectionsMap: Object.fromEntries(sectionsMap),
      business_type: synthesis?.business_type || 'general',
      section_step_order: Object.fromEntries(newSectionStepOrder)
    };

    console.log('💾 Saving reorder data:', {
      sectionsCount: saveData.sections.length,
      sectionsMapKeys: Object.keys(saveData.sectionsMap).length,
      sectionStepOrderKeys: Object.keys(saveData.section_step_order).length,
      newOrderForSection: saveData.section_step_order[sectionId]
    });

    await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, saveData);

    console.log('✅ Section reordering saved successfully');

    // Trigger global refresh to sync all tabs
    if (onRefresh) {
      console.log('🔄 Triggering global refresh after reorder...');
      setTimeout(() => {
        onRefresh();
      }, 200);
    }

    toast.success('Steps reordered successfully', {
      duration: 2000,
      icon: '🔄'
    });

  } catch (error) {
    console.error('❌ Error reordering steps in section:', error);
    toast.error('Failed to reorder steps');

    // Revert local state on error
    setSectionStepOrder(sectionStepOrder);
  }
}, [currentProject?.id, stepsBySection, sectionStepOrder, sections, sectionsMap, synthesis?.business_type, onRefresh]);

  const handleMainFlowDragStart = (e: React.DragEvent, stepIndex: number) => {
    console.log('🚀 Main flow drag start - Step:', stepIndex);

    setDraggedStepId(stepIndex);
    setDraggedFromSection('main-flow');

    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'main-flow-step',
      stepIndex: stepIndex,
      source: 'main-flow'
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  // Reorder two steps within the global steps array, keeping sectionsMap and sectionStepOrder consistent
  const reorderUnassignedStep = async (fromGlobal: number, toGlobal: number) => {
    if (fromGlobal === toGlobal || !currentProject?.id) return;

    // Build new steps array
    const newSteps = [...steps];
    const [moved] = newSteps.splice(fromGlobal, 1);
    newSteps.splice(toGlobal, 0, moved);

    // Build an index remap: oldIndex → newIndex
    const remap = (oldIdx: number): number => {
      if (oldIdx === fromGlobal) return toGlobal;
      if (fromGlobal < toGlobal) {
        if (oldIdx > fromGlobal && oldIdx <= toGlobal) return oldIdx - 1;
      } else {
        if (oldIdx >= toGlobal && oldIdx < fromGlobal) return oldIdx + 1;
      }
      return oldIdx;
    };

    // Update sectionsMap
    const newSectionsMap = new Map<string, string>();
    sectionsMap.forEach((sectionId, indexStr) => {
      newSectionsMap.set(remap(parseInt(indexStr)).toString(), sectionId);
    });

    // Update sectionStepOrder
    const newSectionStepOrder = new Map<string, number[]>();
    sectionStepOrder.forEach((order, sectionId) => {
      newSectionStepOrder.set(sectionId, order.map(remap));
    });

    // Apply local state immediately
    setSteps(newSteps);
    setSectionsMap(newSectionsMap);
    setSectionStepOrder(newSectionStepOrder);
    setDragOverUnassignedIndex(null);

    try {
      await api.put(`/api/analysis/process-steps/${currentProject.id}`, { steps: newSteps });

      if (sections.length > 0) {
        await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
          sections,
          sectionsMap: Object.fromEntries(newSectionsMap),
          business_type: synthesis?.business_type || 'general',
          section_step_order: Object.fromEntries(newSectionStepOrder)
        });
      }

      toast.success('Steps reordered', { duration: 2000 });
      if (onRefresh) setTimeout(() => onRefresh(), 200);
    } catch (error) {
      console.error('❌ Error reordering unassigned steps:', error);
      toast.error('Failed to reorder steps');
      setSteps(steps);
      setSectionsMap(sectionsMap);
      setSectionStepOrder(sectionStepOrder);
    }
  };

  const handleUnassignedDragOver = (e: React.DragEvent, targetGlobalIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverUnassignedIndex(targetGlobalIndex);
  };

  const handleUnassignedDrop = async (e: React.DragEvent, targetGlobalIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverUnassignedIndex(null);
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (dragData.type === 'main-flow-step') {
        await reorderUnassignedStep(dragData.stepIndex, targetGlobalIndex);
      }
    } catch (err) {
      console.error('❌ Unassigned drop parse error:', err);
    } finally {
      setDraggedStepId(null);
      setDraggedFromSection(null);
    }
  };

  useEffect(() => {
    if (synthesis?.section_step_order) {
      console.log('📥 Loading section step order from backend:', synthesis.section_step_order);
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);
      console.log('✅ Section step order loaded:', Object.fromEntries(orderMap));
    }
  }, [synthesis?.section_step_order]);

  const saveSectionOrder = useCallback(async (newSectionStepOrder: Map<string, number[]>) => {
    if (!currentProject?.id) return;

    try {
      const sectionOrderObj = Object.fromEntries(
        Array.from(newSectionStepOrder.entries()).map(([secId, order]) => [secId, order])
      );

      console.log('💾 Saving section order to backend:', sectionOrderObj);

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(sectionsMap),
        sectionStepOrder: sectionOrderObj,
        business_type: synthesis?.business_type || 'general'
      });

      console.log('✅ Section order saved successfully');
    } catch (error) {
      console.error('❌ Error saving section order:', error);
    }
  }, [currentProject?.id, sections, sectionsMap, synthesis?.business_type]);

  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  if (shouldShowLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <ProcessSynthesisLoading
          isAnalyzing={isAnalyzing}
          analysisProgress={analysisProgress}
        />
      </div>
    );
  }


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-super-dark-purple">Process Synthesis</h2>
        <div className="flex items-center space-x-3">
          {isSaving && (
            <span className="animate-spin w-4 h-4 border-b-2 border-blue-500 rounded-full" />
          )}
          {steps.length > 0 && (
            <button
              onClick={exportToExcel}
              disabled={isExporting}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export Process Synthesis to Excel"
            >
              {isExporting ? (
                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>{isExporting ? 'Exporting...' : 'Export Excel'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Manual Section Creation */}
      {steps.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Section Management
            </h3>
            <button
              onClick={() => setShowAddSection(!showAddSection)}
              className="flex items-center space-x-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Section</span>
            </button>
          </div>

          {/* Add New Section Form */}
          {showAddSection && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-3">Create New Section</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="Section name (e.g., 'Initiation', 'Processing', 'Validation')"
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && newSectionName.trim() && addSection()}
                />
                <input
                  type="text"
                  value={newSectionDescription}
                  onChange={(e) => setNewSectionDescription(e.target.value)}
                  placeholder="Section description (optional)"
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && newSectionName.trim() && addSection()}
                />
                <div className="flex space-x-2">
                  <button
                    onClick={addSection}
                    disabled={!newSectionName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create Section</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowAddSection(false);
                      setNewSectionName('');
                      setNewSectionDescription('');
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Existing Sections List */}
          {sections.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Current Sections:</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {sections.map(section => (
                  <div key={section.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: section.color }}
                      />
                      <div>
                        <span className="font-medium">{section.name}</span>
                        {section.description && (
                          <p className="text-sm text-gray-500">{section.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {stepsBySection.get(section.id)?.length || 0} steps
                      </span>
                      <button
                        onClick={() => deleteSection(section.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title="Delete section"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Process Sections with Steps */}
      {(sections.length > 0 || steps.length > 0) && (
        <div className="space-y-4">
          {sections.map(section => {
            const sectionSteps = stepsBySection.get(section.id) || [];
            const isExpanded = expandedSections.has(section.id);

            return (
              <div key={section.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {/* Section Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    const newExpanded = new Set(expandedSections);
                    if (isExpanded) {
                      newExpanded.delete(section.id);
                    } else {
                      newExpanded.add(section.id);
                    }
                    setExpandedSections(newExpanded);
                  }}
                  style={{ borderLeftColor: section.color, borderLeftWidth: '4px' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                        {sectionSteps.length > 0 ? (
                          <FolderOpen className="w-5 h-5" style={{ color: section.color }} />
                        ) : (
                          <Folder className="w-5 h-5 text-gray-400" />
                        )}
                        <h3 className="font-semibold text-gray-800">{section.name}</h3>
                      </div>
                      {section.description && (
                        <span className="text-sm text-gray-500">({section.description})</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                        {sectionSteps.length} steps
                      </span>
                      {sectionSteps.length === 0 && (
                        <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                          Empty - Drop steps here
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section Steps */}
                {isExpanded && (
                  <div
                    className="p-4 pt-0 space-y-2 min-h-[100px] border-2 border-dashed border-gray-200 bg-gray-50"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleSectionContainerDrop(e, section.id)}
                  >
                    {sectionSteps.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <FolderOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>Drop steps here</p>
                        <p className="text-xs">Drag steps from "End-to-End Process Flow" or other sections</p>
                      </div>
                    ) : (
                      sectionSteps.map(({ step, index }, positionInSection) => {
                        const stepText = typeof step === 'string' ? step : (step?.name || step?.step || '');
                        const isDragging = draggedStepId === index;

                        return (
                          <div
                            key={`section_step_${index}`}
                            draggable
                            onDragStart={(e) => handleSectionStepDragStart(e, index, section.id)}
                            onDragOver={handleSectionStepDragOver}
                            onDrop={(e) => handleSectionStepDrop(e, index, section.id)}
                            className={`p-3 bg-white rounded-md border transition-all cursor-move shadow-sm ${isDragging
                                ? 'border-blue-300 bg-blue-50 opacity-50'
                                : 'border-gray-200 hover:border-gray-300'
                              }`}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="mt-1 text-gray-400 hover:text-gray-600">
                                <GripVertical className="w-4 h-4" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Step {getSequentialStepNumber(index, section.id)}
                                  </span>
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={() => {
                                        setEditingStep(index);
                                        setEditValue(stepText);
                                      }}
                                      className="text-gray-400 hover:text-blue-600 transition-colors"
                                      title="Edit step"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => deleteStep(index)}
                                      className="text-gray-400 hover:text-red-600 transition-colors"
                                      title="Delete step"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setChatStepIdx(index);
                                        setChatOpen(true);
                                      }}
                                      className="text-gray-400 hover:text-green-600 transition-colors"
                                      title="Ask Jarvis about this step"
                                    >
                                      <MessageCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                {editingStep === index ? (
                                  <div className="mt-2 space-y-2">
                                    <textarea
                                      ref={(el) => {
                                        if (el && editingStep === index) {
                                          el.style.pointerEvents = 'auto';
                                          el.style.userSelect = 'text';
                                          el.style.webkitUserSelect = 'text';
                                          (el.style as any).MozUserSelect = 'text';
                                          (el.style as any).msUserSelect = 'text';
                                          el.style.cursor = 'text';
                                        }
                                      }}
                                      autoFocus
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        // Handle keyboard shortcuts
                                        if (e.key === 'Enter' && e.ctrlKey) {
                                          e.preventDefault();
                                          // Trigger save button click
                                          e.currentTarget.parentElement?.querySelector('button')?.click();
                                        } else if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setEditingStep(null);
                                          setEditValue('');
                                        }
                                        // Allow normal text operations
                                        if (!['Enter', 'Escape', 'Tab'].includes(e.key)) {
                                          e.stopPropagation();
                                        }
                                      }}
                                      onMouseDown={(e) => {
                                        // Ensure mouse events work for text selection
                                        e.stopPropagation();
                                      }}
                                      onDoubleClick={(e) => {
                                        // Enable double-click to select words
                                        e.stopPropagation();
                                      }}
                                      className="w-full p-3 border border-gray-300 rounded-md resize-none focus:outline-none 
             focus:ring-2 focus:ring-blue-500 focus:border-blue-500
             min-h-[100px] text-sm leading-relaxed bg-white
             selection:bg-blue-200 selection:text-blue-900"
                                      style={{
                                        pointerEvents: 'auto',
                                        userSelect: 'text',
                                        WebkitUserSelect: 'text',
                                        cursor: 'text'
                                      } as React.CSSProperties}
                                      rows={4}
                                      placeholder="Enter step description..."
                                    />
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={async () => {
                                          if (!currentProject?.id) {
                                            toast.error('No project selected');
                                            return;
                                          }

                                          try {
                                            console.log('🔄 EnhancedProcessSynthesisTab: Saving edited step...', { index, editValue: editValue.substring(0, 50) + '...' });

                                            const updatedSteps = [...steps];
                                            if (typeof updatedSteps[index] === 'string') {
                                              updatedSteps[index] = editValue;
                                            } else {
                                              (updatedSteps[index] as ProcessStep).step = editValue;
                                            }

                                            // Update local state immediately
                                            setSteps(updatedSteps);
                                            setEditingStep(null);
                                            setEditValue('');

                                            // CRITICAL: Save to backend immediately
                                            await api.put(`/api/analysis/process-steps/${currentProject.id}`, {
                                              steps: updatedSteps
                                            });

                                            console.log('✅ EnhancedProcessSynthesisTab: Step saved to backend successfully');

                                            // CRITICAL: Trigger global refresh for tab synchronization
                                            if (onRefresh) {
                                              console.log('🔄 EnhancedProcessSynthesisTab: Triggering global refresh for tab sync...');
                                              setTimeout(() => {
                                                onRefresh();
                                              }, 200); // Small delay to ensure backend save completes
                                            }

                                            toast.success('Step updated and synchronized across tabs', {
                                              duration: 3000,
                                              icon: '✅'
                                            });

                                          } catch (error) {
                                            console.error('❌ EnhancedProcessSynthesisTab: Failed to save step:', error);
                                            toast.error('Failed to save step. Please try again.');

                                            // Revert local state on error
                                            setEditingStep(index);
                                            setEditValue(editValue);
                                          }
                                        }}
                                        className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingStep(null);
                                          setEditValue('');
                                        }}
                                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-700 mt-1">{stepText}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Unassigned Steps */}
      {unassignedSteps.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-amber-500"
            onClick={() => setUnassignedStepsExpanded(!unassignedStepsExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {unassignedStepsExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  <Folder className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-800">End-to-End Process Flow</h3>
                </div>
                <span className="text-sm text-gray-600">(Drag these to sections)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                  {unassignedSteps.length} steps
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); setIsAddingStep(true); setUnassignedStepsExpanded(true); }}
                  className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  title="Add step manually"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </button>
              </div>
            </div>
          </div>

          {unassignedStepsExpanded && (
            <div className="p-4 pt-0 space-y-2 bg-gray-50">
              {unassignedSteps.map(({ step, index }) => {
                const stepText = typeof step === 'string' ? step : (step?.name || step?.step || '');

                return (
                  <div
                    key={`unassigned_step_${index}`}
                    draggable
                    onDragStart={(e) => handleMainFlowDragStart(e, index)}
                    onDragOver={(e) => handleUnassignedDragOver(e, index)}
                    onDragLeave={() => setDragOverUnassignedIndex(null)}
                    onDrop={(e) => handleUnassignedDrop(e, index)}
                    onDragEnd={() => { setDraggedStepId(null); setDragOverUnassignedIndex(null); }}
                    className={`p-3 bg-white rounded-md border transition-colors cursor-move shadow-sm ${
                      dragOverUnassignedIndex === index && draggedStepId !== index
                        ? 'border-blue-400 bg-blue-50'
                        : draggedStepId === index
                        ? 'border-blue-300 bg-blue-50 opacity-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="mt-1 text-gray-400 hover:text-gray-600">
                        <GripVertical className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            Step {index + 1}
                          </span>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => {
                                setEditingStep(index);
                                setEditValue(stepText);
                              }}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                              title="Edit step"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteStep(index)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete step"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setChatStepIdx(index);
                                setChatOpen(true);
                              }}
                              className="text-gray-400 hover:text-green-600 transition-colors"
                              title="Ask Jarvis about this step"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {editingStep === index ? (
                          <div className="mt-2 space-y-2">
                            <textarea
                              ref={(el) => {
                                if (el && editingStep === index) {
                                  el.style.pointerEvents = 'auto';
                                  el.style.userSelect = 'text';
                                  el.style.webkitUserSelect = 'text';
                                  (el.style as any).MozUserSelect = 'text';
                                  (el.style as any).msUserSelect = 'text';
                                  el.style.cursor = 'text';
                                }
                              }}
                              autoFocus
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                // Handle keyboard shortcuts
                                if (e.key === 'Enter' && e.ctrlKey) {
                                  e.preventDefault();
                                  // Trigger save button click
                                  e.currentTarget.parentElement?.querySelector('button')?.click();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setEditingStep(null);
                                  setEditValue('');
                                }
                                // Allow normal text operations
                                if (!['Enter', 'Escape', 'Tab'].includes(e.key)) {
                                  e.stopPropagation();
                                }
                              }}
                              onMouseDown={(e) => {
                                // Ensure mouse events work for text selection
                                e.stopPropagation();
                              }}
                              onDoubleClick={(e) => {
                                // Enable double-click to select words
                                e.stopPropagation();
                              }}
                              className="w-full p-3 border border-gray-300 rounded-md resize-none focus:outline-none 
             focus:ring-2 focus:ring-blue-500 focus:border-blue-500
             min-h-[100px] text-sm leading-relaxed bg-white
             selection:bg-blue-200 selection:text-blue-900"
                              style={{
                                pointerEvents: 'auto',
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                                cursor: 'text'
                              } as React.CSSProperties}
                              rows={4}
                              placeholder="Enter step description..."
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={async () => {
                                  if (!currentProject?.id) {
                                    toast.error('No project selected');
                                    return;
                                  }

                                  try {
                                    console.log('🔄 EnhancedProcessSynthesisTab: Saving edited step...', { index, editValue: editValue.substring(0, 50) + '...' });

                                    const updatedSteps = [...steps];
                                    if (typeof updatedSteps[index] === 'string') {
                                      updatedSteps[index] = editValue;
                                    } else {
                                      (updatedSteps[index] as ProcessStep).step = editValue;
                                    }

                                    // Update local state immediately
                                    setSteps(updatedSteps);
                                    setEditingStep(null);
                                    setEditValue('');

                                    // CRITICAL: Save to backend immediately
                                    await api.put(`/api/analysis/process-steps/${currentProject.id}`, {
                                      steps: updatedSteps
                                    });

                                    console.log('✅ EnhancedProcessSynthesisTab: Step saved to backend successfully');

                                    // CRITICAL: Trigger global refresh for tab synchronization
                                    if (onRefresh) {
                                      console.log('🔄 EnhancedProcessSynthesisTab: Triggering global refresh for tab sync...');
                                      setTimeout(() => {
                                        onRefresh();
                                      }, 200); // Small delay to ensure backend save completes
                                    }

                                    toast.success('Step updated and synchronized across tabs', {
                                      duration: 3000,
                                      icon: '✅'
                                    });

                                  } catch (error) {
                                    console.error('❌ EnhancedProcessSynthesisTab: Failed to save step:', error);
                                    toast.error('Failed to save step. Please try again.');

                                    // Revert local state on error
                                    setEditingStep(index);
                                    setEditValue(editValue);
                                  }
                                }}
                                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingStep(null);
                                  setEditValue('');
                                }}
                                className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="mt-1 text-gray-700">{stepText}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {isAddingStep && (
                <div className="p-3 bg-white rounded-md border border-blue-300 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-700 mb-1 block">Step {steps.length + 1}</span>
                      <textarea
                        value={newStepValue}
                        onChange={(e) => setNewStepValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); addStep(); }
                          if (e.key === 'Escape') { setIsAddingStep(false); setNewStepValue(''); }
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-full p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-[80px]"
                        placeholder="Enter step description... (Ctrl+Enter to save, Esc to cancel)"
                        autoFocus
                        rows={3}
                      />
                      <div className="flex space-x-2 mt-2">
                        <button
                          onClick={addStep}
                          className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setIsAddingStep(false); setNewStepValue(''); }}
                          className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Additional Analysis Cards */}
      {/* Note: Stakeholders & Systems moved to Analysis > Stakeholders & Systems tab */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Stakeholders & Responsibilities - Moved to Analysis tab */}
        {false && Object.keys(stakeholders).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-white border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  Stakeholders & Responsibilities
                </h3>
                <div className="flex items-center space-x-2">
                  {selectedStakeholders.length > 1 && (
                    <button
                      onClick={mergeStakeholders}
                      className="text-purple-600 hover:text-purple-800 p-1"
                      title="Merge selected stakeholders"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8 5a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1zM6 12a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1zM4 16a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => setShowAddStakeholder(!showAddStakeholder)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Add stakeholder"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              {/* Add Stakeholder Form */}
              {showAddStakeholder && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">Add New Stakeholder</h4>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Role/Position"
                      value={newStakeholder.role}
                      onChange={(e) => setNewStakeholder({ ...newStakeholder, role: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <textarea
                      placeholder="Responsibilities"
                      value={newStakeholder.responsibility}
                      onChange={(e) => setNewStakeholder({ ...newStakeholder, responsibility: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={addStakeholder}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowAddStakeholder(false);
                          setNewStakeholder({ role: '', responsibility: '' });
                        }}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Stakeholders List */}
              <div className="space-y-3">
                {Object.entries(stakeholders).map(([role, responsibility]) => {
                  // Handle both string and object formats
                  let responsibilityText: string;

                  if (typeof responsibility === 'string') {
                    responsibilityText = responsibility;
                  } else if (responsibility && typeof responsibility === 'object') {
                    const respObj = responsibility as any;
                    responsibilityText = respObj.responsibility || respObj.description || respObj.details || JSON.stringify(responsibility);
                  } else {
                    responsibilityText = String(responsibility || '');
                  }

                  return (
                    <div key={role} className="group p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                      {editingStakeholder === role ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editStakeholderValue.role}
                            onChange={(e) => setEditStakeholderValue({ ...editStakeholderValue, role: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <textarea
                            value={editStakeholderValue.responsibility}
                            onChange={(e) => setEditStakeholderValue({ ...editStakeholderValue, responsibility: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={2}
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={saveStakeholderEdit}
                              className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingStakeholder(null)}
                              className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedStakeholders.includes(role)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedStakeholders([...selectedStakeholders, role]);
                                  } else {
                                    setSelectedStakeholders(selectedStakeholders.filter(r => r !== role));
                                  }
                                }}
                                className="mr-2"
                              />
                              <div className="font-medium text-gray-800">{role}</div>
                            </div>
                            <div className="text-sm text-gray-600 mt-1 ml-6">{responsibilityText}</div>
                          </div>
                          <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditStakeholder(role)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit stakeholder"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => deleteStakeholder(role)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete stakeholder"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Systems & Platforms - Moved to Analysis tab */}
        {false && Object.keys(systems).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-white border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                  Systems & Platforms
                </h3>
                <div className="flex items-center space-x-2">
                  {selectedSystems.length > 1 && (
                    <button
                      onClick={mergeSystems}
                      className="text-purple-600 hover:text-purple-800 p-1"
                      title="Merge selected systems"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8 5a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1zM6 12a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1zM4 16a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => setShowAddSystem(!showAddSystem)}
                    className="text-green-600 hover:text-green-800 p-1"
                    title="Add system"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              {/* Add System Form */}
              {showAddSystem && (
                <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-medium text-green-800 mb-2">Add New System</h4>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="System/Platform Name"
                      value={newSystem.system}
                      onChange={(e) => setNewSystem({ ...newSystem, system: e.target.value })}
                      className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <textarea
                      placeholder="Purpose/Description"
                      value={newSystem.purpose}
                      onChange={(e) => setNewSystem({ ...newSystem, purpose: e.target.value })}
                      className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={2}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={addSystem}
                        className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowAddSystem(false);
                          setNewSystem({ system: '', purpose: '' });
                        }}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {Object.keys(systems).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(systems).map(([system, details]) => {
                    // Only extract and display the exact data that exists
                    let systemData: any = {};

                    if (typeof details === 'string') {
                      systemData = { purpose: details };
                    } else if (details && typeof details === 'object') {
                      systemData = details as any;
                    }

                    return (
                      <div key={system} className="group p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                        {editingSystem === system ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editSystemValue.system}
                              onChange={(e) => setEditSystemValue({ ...editSystemValue, system: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <textarea
                              value={editSystemValue.purpose}
                              onChange={(e) => setEditSystemValue({ ...editSystemValue, purpose: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                              rows={2}
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={saveSystemEdit}
                                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingSystem(null)}
                                className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={selectedSystems.includes(system)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedSystems([...selectedSystems, system]);
                                    } else {
                                      setSelectedSystems(selectedSystems.filter(s => s !== system));
                                    }
                                  }}
                                  className="mr-2"
                                />
                                <div className="font-medium text-gray-800">{system}</div>
                              </div>
                              <div className="text-sm text-gray-600 mt-1 ml-6">
                                {/* Display only the exact data fields that exist */}
                                {systemData.purpose && (
                                  <div><strong>Purpose:</strong> {systemData.purpose}</div>
                                )}
                                {systemData.type && (
                                  <div><strong>Type:</strong> {systemData.type}</div>
                                )}
                                {systemData.integration_points && (
                                  <div><strong>Integration Points:</strong> {
                                    Array.isArray(systemData.integration_points)
                                      ? systemData.integration_points.join(' | ')
                                      : systemData.integration_points
                                  }</div>
                                )}
                                {systemData.criticality && (
                                  <div><strong>Criticality:</strong> {systemData.criticality}</div>
                                )}
                                {/* Type badge */}
                                {systemData.type && (
                                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                                    {systemData.type}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => startEditSystem(system)}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Edit system"
                              >
                                <Edit className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => deleteSystem(system)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete system"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No systems identified yet</p>
              )}
            </div>
          </div>
        )}

        {/* Control Framework */}
        {synthesisData?.control_framework && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-white border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                Control Framework
                <Tooltip 
                  text={`Control Framework represents the systematic approach to identifying, implementing, and monitoring controls within business processes.\n\nHow Controls are Identified:\n- Regulatory requirements (SOX, Basel III, GDPR)\n- Industry best practices and standards\n- Risk assessment outcomes\n- Operational policy requirements\n- Segregation of duties principles\n\nControls include:\n- Preventive controls (authorization, approval workflows)\n- Detective controls (monitoring, reconciliation)\n- Corrective controls (exception handling, remediation)\n\nEach control is mapped to specific process steps to ensure comprehensive coverage and compliance.`} 
                  placement="top"
                >
                  <Info className="w-4 h-4 ml-2 text-gray-500 cursor-pointer hover:text-purple-600 transition-colors" />
                </Tooltip>
              </h3>
            </div>
            <div className="p-6">
              {Array.isArray(synthesisData.control_framework) ? (
                <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-3">
                  {synthesisData.control_framework.map((control: string, index: number) => (
                    <div key={index} className="bg-white p-3 rounded-lg border-l-4 border-purple-500 shadow-sm">
                      <p className="text-sm text-gray-700">{control}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(synthesisData.control_framework).map(([control, description], index) => {
                    let descriptionText: string;

                    if (typeof description === 'string') {
                      descriptionText = description;
                    } else if (description && typeof description === 'object') {
                      const descObj = description as any;
                      descriptionText = descObj.description || descObj.purpose || descObj.details || JSON.stringify(description);
                    } else {
                      descriptionText = String(description || '');
                    }

                    return (
                      <div key={index} className="bg-white p-4 rounded-lg border-l-4 border-purple-500 shadow-sm">
                        <div className="flex items-start">
                          <span className="bg-purple-100 text-purple-800 text-sm font-medium px-2 py-1 rounded mr-3 flex-shrink-0">
                            {index + 1}
                          </span>
                          <div>
                            <div className="font-medium text-gray-800">{control}</div>
                            {descriptionText && (
                              <div className="text-sm text-gray-600 mt-1">{descriptionText}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Compliance Score Card */}
      {/* {globalComplianceScore !== null && globalComplianceScore > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-white border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
              Compliance Score
            </h3>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="w-32 h-32 rounded-full border-8 border-gray-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800">
                      {globalComplianceScore}%
                    </div>
                    <div className="text-sm text-gray-500">Compliance</div>
                  </div>
                </div>
                <div
                  className="absolute top-0 left-0 w-32 h-32 rounded-full border-8 border-transparent"
                  style={{
                    borderTopColor: globalComplianceScore >= 80 ? '#10B981' : globalComplianceScore >= 60 ? '#F59E0B' : '#EF4444',
                    transform: `rotate(${globalComplianceScore * 3.6}deg)`,
                    borderRightColor: globalComplianceScore >= 25 ? (globalComplianceScore >= 80 ? '#10B981' : globalComplianceScore >= 60 ? '#F59E0B' : '#EF4444') : 'transparent'
                  }}
                ></div>
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Overall compliance score based on process analysis and template alignment
              </p>
            </div>
          </div>
        </div>
      )} */}

      {/* Empty State */}
      {steps.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No process steps found. Run analysis to generate process synthesis.</p>
        </div>
      )}

      {/* Enhanced StepChatModal for AI Q&A about a process step */}
      {chatStepIdx !== null && (() => {
        const stepVal = steps && Array.isArray(steps) ? steps[chatStepIdx] : null;
        const stepText = typeof stepVal === 'string' ? stepVal : ((stepVal as any)?.name || (stepVal as ProcessStep)?.step || '');

        return (
          <EnhancedStepChatModal
            open={chatOpen}
            onClose={() => {
              setChatOpen(false);
              setChatStepIdx(null);
            }}
            step={stepText}
            projectId={currentProject?.id}
            stepIndex={chatStepIdx}
            onStepsUpdated={onRefresh}
          />
        );
      })()}
    </div>
  );
};

export default EnhancedProcessSynthesisTab;